import { db } from './firebase';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';

export const trainService = {
  // Add a new train (Admin only)
  addTrain: async (trainData) => {
    const trainId = `TRAIN_${Date.now()}`;
    const trainRef = doc(db, 'trains', trainId);

    const seats = [];
    // Create seat array (e.g., 10 coaches with 72 seats each)
    for (let coach = 1; coach <= trainData.coaches || 10; coach++) {
      const coachSeats = [];
      for (let seatNum = 1; seatNum <= trainData.seatsPerCoach || 72; seatNum++) {
        coachSeats.push({
          seatNumber: seatNum,
          status: 'available', // available, booked, rac, waiting
          passenger: null,
        });
      }
      seats.push({
        coachNumber: coach,
        coachType: trainData.coachType || 'General',
        seats: coachSeats,
        totalSeats: coachSeats.length,
        availableSeats: coachSeats.length,
      });
    }

    await setDoc(trainRef, {
      trainId,
      trainName: trainData.trainName,
      trainNumber: trainData.trainNumber,
      source: trainData.source,
      destination: trainData.destination,
      route: trainData.route, // Array of stations
      departureTime: trainData.departureTime,
      arrivalTime: trainData.arrivalTime,
      distance: trainData.distance,
      baseFare: trainData.baseFare,
      totalCoaches: trainData.coaches || 10,
      totalSeats: (trainData.coaches || 10) * (trainData.seatsPerCoach || 72),
      availableSeats: (trainData.coaches || 10) * (trainData.seatsPerCoach || 72),
      seats,
      operatingDays: trainData.operatingDays, // Array of days (MON, TUE, etc.)
      status: 'active',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    return trainId;
  },

  // Get all trains
  getAllTrains: async () => {
    const trainsRef = collection(db, 'trains');
    const snapshot = await getDocs(trainsRef);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  },

  // Search trains
  searchTrains: async (source, destination, date) => {
    const q = query(
      collection(db, 'trains'),
      where('source', '==', source),
      where('destination', '==', destination),
      where('status', '==', 'active')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  },

  // Get train details
  getTrainDetails: async (trainId) => {
    const trainDoc = await getDoc(doc(db, 'trains', trainId));
    return trainDoc.exists() ? { id: trainDoc.id, ...trainDoc.data() } : null;
  },

  // Update train
  updateTrain: async (trainId, updateData) => {
    await updateDoc(doc(db, 'trains', trainId), {
      ...updateData,
      updatedAt: Timestamp.now(),
    });
  },

  // Delete train (Admin only)
  deleteTrain: async (trainId) => {
    await deleteDoc(doc(db, 'trains', trainId));
  },

  // Get seat layout for a train
  getSeatLayout: async (trainId) => {
    const train = await trainService.getTrainDetails(trainId);
    return train?.seats || [];
  },

  // Check seat availability
  checkSeatAvailability: async (trainId, coachNumber, seatNumber) => {
    const train = await trainService.getTrainDetails(trainId);
    if (!train) return null;

    const coach = train.seats.find(c => c.coachNumber === coachNumber);
    if (!coach) return null;

    const seat = coach.seats.find(s => s.seatNumber === seatNumber);
    return seat?.status || null;
  },
};

export default trainService;
